import { GameState } from '../../core/Game';
import { toCanvasCoords } from '../../utils/canvas';

interface RendererLike {
    getCanvasWidth(): number;
    getCanvasHeight(): number;
    isLandscape: boolean;
}

interface InputManagerLike {
    cancelTouchConfirm(): void;
}

interface GameLike {
    currentState: GameState;
    getRenderer(): RendererLike;
    getInputManager(): InputManagerLike;
    setState(state: GameState): void;
}

interface ButtonArea {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface ScreenState {
    isPaused: boolean;
    isGameOver: boolean;
    showGameOverUI: boolean;
    buttonClickHandled: boolean;
    pauseContinueButton: ButtonArea;
    pauseTitleButton: ButtonArea;
    restartButton: ButtonArea;
    titleButton: ButtonArea;
    onResume: () => void;
    onTitle: () => void;
    onRestart: () => void;
}

type PressedButtonType = 'pauseContinue' | 'pauseTitle' | 'restart' | 'title' | null;

/**
 * GameScreen의 입력 처리 담당
 */
export class GameInputHandler {
    private game: GameLike;
    private screenState: ScreenState;
    private pressedButton: PressedButtonType;
    private boundHandleClick: ((e: MouseEvent) => void) | null = null;
    private boundHandleTouchStart: ((e: TouchEvent) => void) | null = null;
    private boundHandleTouchEnd: ((e: TouchEvent) => void) | null = null;
    private listenersSetup: boolean = false;

    constructor(game: GameLike, screenState: ScreenState) {
        this.game = game;
        this.screenState = screenState;
        this.pressedButton = null;
        this.setupListeners();
    }

    setupListeners(): void {
        if (this.listenersSetup) return;

        const canvas = document.getElementById('gameCanvas') as HTMLCanvasElement | null;
        if (!canvas) return;

        this.boundHandleClick = (e: MouseEvent) => this.handleClick(e);
        this.boundHandleTouchStart = (e: TouchEvent) => this.handleTouchStart(e);
        this.boundHandleTouchEnd = (e: TouchEvent) => this.handleTouchEnd(e);

        canvas.addEventListener('click', this.boundHandleClick);
        canvas.addEventListener('touchstart', this.boundHandleTouchStart, { capture: true });
        canvas.addEventListener('touchend', this.boundHandleTouchEnd, { capture: true });

        this.listenersSetup = true;
    }

    cleanup(): void {
        const canvas = document.getElementById('gameCanvas') as HTMLCanvasElement | null;
        if (!canvas) return;

        if (this.boundHandleClick) {
            canvas.removeEventListener('click', this.boundHandleClick);
        }
        if (this.boundHandleTouchStart) {
            canvas.removeEventListener('touchstart', this.boundHandleTouchStart, { capture: true });
        }
        if (this.boundHandleTouchEnd) {
            canvas.removeEventListener('touchend', this.boundHandleTouchEnd, { capture: true });
        }

        this.boundHandleClick = null;
        this.boundHandleTouchStart = null;
        this.boundHandleTouchEnd = null;
        this.listenersSetup = false;
    }

    isInsideButton(x: number, y: number, button: ButtonArea): boolean {
        // 버튼 영역이 유효한지 체크 (width와 height가 0보다 커야 함)
        if (button.width <= 0 || button.height <= 0) {
            return false;
        }
        return x >= button.x && x <= button.x + button.width &&
               y >= button.y && y <= button.y + button.height;
    }

    getTouchPosition(touch: Touch): { x: number; y: number } {
        const canvas = document.getElementById('gameCanvas') as HTMLCanvasElement;
        const rect = canvas.getBoundingClientRect();
        const renderer = this.game.getRenderer();
        return toCanvasCoords(touch.clientX, touch.clientY, rect, renderer);
    }

    handleClick(e: MouseEvent): void {
        if (this.game.currentState !== GameState.PLAYING) return;

        const canvas = document.getElementById('gameCanvas') as HTMLCanvasElement;
        const rect = canvas.getBoundingClientRect();
        const renderer = this.game.getRenderer();
        const pos = toCanvasCoords(e.clientX, e.clientY, rect, renderer);

        // 일시정지 버튼 클릭
        if (this.screenState.isPaused && !this.screenState.isGameOver) {
            if (this.isInsideButton(pos.x, pos.y, this.screenState.pauseContinueButton)) {
                this.screenState.onResume();
                return;
            }
            if (this.isInsideButton(pos.x, pos.y, this.screenState.pauseTitleButton)) {
                this.screenState.onTitle();
                return;
            }
        }

        // 게임오버 버튼 클릭
        if (!this.screenState.showGameOverUI) return;

        if (this.isInsideButton(pos.x, pos.y, this.screenState.restartButton)) {
            this.screenState.onRestart();
        } else if (this.isInsideButton(pos.x, pos.y, this.screenState.titleButton)) {
            this.game.setState(GameState.TITLE);
        }
    }

    handleTouchStart(e: TouchEvent): void {
        if (this.game.currentState !== GameState.PLAYING) {
            this.pressedButton = null;
            return;
        }

        const touch = e.changedTouches[0];
        const pos = this.getTouchPosition(touch);

        // 일시정지 버튼
        if (this.screenState.isPaused && !this.screenState.isGameOver) {
            if (this.isInsideButton(pos.x, pos.y, this.screenState.pauseContinueButton)) {
                this.pressedButton = 'pauseContinue';
            } else if (this.isInsideButton(pos.x, pos.y, this.screenState.pauseTitleButton)) {
                this.pressedButton = 'pauseTitle';
            } else {
                this.pressedButton = null;
            }
            // 일시정지 상태에서는 모든 터치 이벤트 차단
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        // 게임오버 상태
        if (this.screenState.showGameOverUI) {
            if (this.isInsideButton(pos.x, pos.y, this.screenState.restartButton)) {
                this.pressedButton = 'restart';
                this.screenState.buttonClickHandled = true;
                this.game.getInputManager().cancelTouchConfirm();
            } else if (this.isInsideButton(pos.x, pos.y, this.screenState.titleButton)) {
                this.pressedButton = 'title';
                this.screenState.buttonClickHandled = true;
                this.game.getInputManager().cancelTouchConfirm();
            } else {
                this.pressedButton = null;
            }
            // 게임오버 상태에서는 모든 터치 이벤트 차단
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        this.pressedButton = null;
    }

    handleTouchEnd(e: TouchEvent): void {
        if (this.game.currentState !== GameState.PLAYING) {
            this.pressedButton = null;
            return;
        }

        const touch = e.changedTouches[0];
        const pos = this.getTouchPosition(touch);

        // 일시정지 상태에서는 모든 터치 이벤트 차단
        if (this.screenState.isPaused && !this.screenState.isGameOver) {
            if (this.pressedButton === 'pauseContinue' && this.isInsideButton(pos.x, pos.y, this.screenState.pauseContinueButton)) {
                this.screenState.onResume();
            } else if (this.pressedButton === 'pauseTitle' && this.isInsideButton(pos.x, pos.y, this.screenState.pauseTitleButton)) {
                this.screenState.onTitle();
            }
            this.pressedButton = null;
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        // 게임오버 상태에서는 모든 터치 이벤트 차단
        if (this.screenState.showGameOverUI) {
            if (this.pressedButton === 'restart' && this.isInsideButton(pos.x, pos.y, this.screenState.restartButton)) {
                this.screenState.onRestart();
            } else if (this.pressedButton === 'title' && this.isInsideButton(pos.x, pos.y, this.screenState.titleButton)) {
                this.game.setState(GameState.TITLE);
            }
            this.pressedButton = null;
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        this.pressedButton = null;
    }
}
